import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BusinessCard from '@/components/base/BusinessCard';
import { categories } from '@/mocks/categories';
import { businesses } from '@/mocks/businesses';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const category = categories.find((c) => c.slug === slug);
  const categoryBusinesses = businesses.filter((b) => b.category === slug);

  const filteredBusinesses = searchQuery
    ? categoryBusinesses.filter(
        (b) =>
          b.name.includes(searchQuery) ||
          b.description.includes(searchQuery) ||
          b.tags.some((t) => t.includes(searchQuery))
      )
    : categoryBusinesses;

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center">
                <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
              </div>
              <h1 className="mt-4 text-xl font-semibold text-foreground-900">分类未找到</h1>
              <p className="mt-2 text-sm text-foreground-600">您访问的分类页面不存在</p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </div>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 黄页网站免费入口91`,
            description: category.description,
            url: `https://www.huangye91.com/category/${category.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '黄页网站免费入口91',
              url: 'https://www.huangye91.com',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
              <div className="mt-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${category.icon} text-xl text-primary-500`}></i>
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {category.name}
                  </h1>
                  <p className="mt-1 text-sm text-foreground-600">{category.description}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            {searchQuery && (
              <p className="mb-4 text-sm text-foreground-600">
                搜索 "<strong>{searchQuery}</strong>" 的结果，共找到 {filteredBusinesses.length} 家企业
              </p>
            )}

            {filteredBusinesses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredBusinesses.map((biz) => (
                  <BusinessCard key={biz.id} business={biz} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center">
                  <i className="ri-search-line text-4xl text-foreground-300"></i>
                </div>
                <p className="mt-4 text-sm text-foreground-600">
                  {searchQuery ? '未找到匹配的企业，请尝试其他关键词' : '该分类下暂无企业信息'}
                </p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}